import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import RegisterForm from './pages/RegisterForm';
import Dashboard from './components/Dashboard';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import Home from './pages/Home';
import About from './pages/About';
import Documentation from './pages/Documentation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import MyEventsPage from './pages/MyEventsPage';
import AllEventsPage from './pages/AllEventsPage';
import EventDetails from './pages/EventDetails';
import EditEvent from './pages/EditEvent';
import EditProfile from "./pages/EditProfile";
import Bids from "./pages/Bids";
import EventBids from "./pages/EventBids";
import MyGigs from "./pages/MyGigs";
import Chat from "./pages/Chat";

import { AuthProvider, useAuth } from './context/AuthContext';
import { SuccessFailureProvider } from './context/SuccessFailureContext';
import { EventsProvider } from './context/EventsContext';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import PrivateRoute from "./components/PrivateRoute";
import PublicProfile from "./pages/PublicProfile";
import SuccessModal from './components/SuccessModal';
import FailureModal from './components/FailureModal';
import NotificationsPage from "./pages/NotificationsPage";
import TestFormBuilder from "./pages/TestFormBuilder";
import FormBuilderPage from "./pages/FormBuilderPage";
import AdminDashboard from "./pages/AdminDashboard";
import FormResponsePage from "./pages/FormResponsePage";

function App() {
  const [pageTitle, setPageTitle] = useState('EventsAwa');

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <SuccessFailureProvider>
            <EventsProvider>
              <ErrorBoundary>
                <Navbar pageTitle={pageTitle} setPageTitle={setPageTitle} />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/register" element={<RegisterForm />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/my-events" element={<MyEventsPage />} />
                  <Route path="/all-events" element={<AllEventsPage />} />
                  <Route path="/documentation" element={<Documentation />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/events/:event_slug" element={<EventDetails />} />
                  <Route path="/events/:event_slug/update" element={<EditEvent />} />
                  <Route path="/profile/:slug" element={<PublicProfile />} />
                  <Route element={<PrivateRoute />}>
                    <Route path="/profile/edit" element={<EditProfile />} />
                  </Route>
                  <Route path="/about" element={<About />} />
                  <Route path="/bids" element={<Bids />} />
                  <Route path="/bids/:eventId" element={<EventBids />} />
                  <Route path="/my-gigs" element={<MyGigs />} />
                  <Route path="/chat/:eventId/:recipientId" element={<Chat />} />
                  <Route path="/notifications" element={
                    // <PrivateRoute>
                    <NotificationsPage />
                    // </PrivateRoute>
                  } />
                  <Route path="/form-builder/:eventId" element={
                    <PrivateRoute>
                      <FormBuilderPage />
                    </PrivateRoute>
                  } />
                  {/* Keep the old route for backward compatibility */}
                  <Route path="/test-form-builder/:eventId?" element={
                    <PrivateRoute>
                      <TestFormBuilder />
                    </PrivateRoute>
                  } />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/forms/response/:responseId" element={<FormResponsePage />} />
                </Routes>
                <SuccessModal />
                <FailureModal />
              </ErrorBoundary>
            </EventsProvider>
          </SuccessFailureProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
};

// Create a RedirectToPage component to handle redirection based on login status
const RedirectToPage = () => {
  const { isLoggedIn } = useAuth();  // Get the login status from the context
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/dashboard');  // Redirect to dashboard if logged in
    } else {
      navigate('/');  // Redirect to home page if logged out
    }
  }, [isLoggedIn, navigate]);  // Dependency on isLoggedIn to trigger the effect when login state changes

  return null;  // No need to render anything here
};

export default App;
